/**
 * Environment Indicator Utility
 * 
 * Provides styling and configuration for non-production environment indicators
 */

// Available indicator styles
export const INDICATOR_STYLES = {
  FADED_RED: 'faded_red',
  CONSTRUCTION: 'construction',
  REDDISH_GRAY: 'reddish_gray',
  MINIMAL: 'minimal',
  BURNT_SIENNA_STRIPES: 'burnt_sienna_stripes'
};

// Current active style - change this to switch styles
export const ACTIVE_STYLE = INDICATOR_STYLES.BURNT_SIENNA_STRIPES;

/**
 * Get environment information from hostname detection
 * No longer uses environment variables - runtime configuration only
 */
export const getEnvironmentInfo = () => {
  const hostname = window.location.hostname;
  let environment = '';

  if (hostname === 'budgetrecon.com' || hostname === 'www.budgetrecon.com') {
    environment = 'prod';
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    environment = 'local';
  } else {
    const parts = hostname.split('.');
    if (parts.length >= 3 && parts.slice(-2).join('.') === 'budgetrecon.com') {
      environment = parts[0];
    } else {
      environment = hostname;
    }
  }

  const isProduction = environment === 'prod';

  return {
    name: environment,
    isProduction,
    displayName: environment === 'prod' ? 'budgetrecon' : environment
  };
};

/**
 * Get AppBar styling based on active indicator style
 */
export const getAppBarStyling = (isDarkMode, envInfo) => {
  if (envInfo.isProduction) {
    return {}; // No special styling for production
  }

  switch (ACTIVE_STYLE) {
    case INDICATOR_STYLES.BURNT_SIENNA_STRIPES: {
      // v4 board recipe (#1399, ui_kits chrome): burnt-sienna stripes at 22%
      // over the bar surface, with a left solid fade so the metric trio stays
      // readable. Stops are tighter below sm (kit mobile: 30/55 vs 34/62).
      // Bar surface mirrors TOKENS surfaceBar (this util has no theme access).
      const bar = isDarkMode ? '#0b0b0e' : '#eef0f3';
      const stripes = 'repeating-linear-gradient(45deg, color-mix(in srgb, #B25138 22%, transparent) 0 12px, transparent 12px 24px)';
      const fade = (solidPct, endPct) =>
        `linear-gradient(90deg, ${bar} 0%, ${bar} ${solidPct}%, transparent ${endPct}%)`;
      return {
        backgroundColor: bar,
        backgroundImage: {
          xs: `${fade(30, 55)}, ${stripes}`,
          sm: `${fade(34, 62)}, ${stripes}`
        },
        position: 'relative'
      };
    }

    case INDICATOR_STYLES.FADED_RED:
      return {
        background: isDarkMode 
          ? 'linear-gradient(90deg, #8B4A47, #A85A57)'
          : 'linear-gradient(90deg, #E8B4B4, #D2A5A5)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 0, 0, 0.1)',
          pointerEvents: 'none'
        }
      };

    case INDICATOR_STYLES.CONSTRUCTION:
      return {
        // §2.8 exception: construction-tape black carries semantic 'this is broken' weight
        background: isDarkMode
          ? 'linear-gradient(45deg, #B8860B 25%, #000000 25%, #000000 50%, #B8860B 50%, #B8860B 75%, #000000 75%)'
          : 'linear-gradient(45deg, #FFD700 25%, #000000 25%, #000000 50%, #FFD700 50%, #FFD700 75%, #000000 75%)',
        backgroundSize: '20px 20px',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 215, 0, 0.2)',
          pointerEvents: 'none'
        }
      };

    case INDICATOR_STYLES.REDDISH_GRAY:
      return {
        background: isDarkMode 
          ? 'linear-gradient(90deg, #6B4A47, #7A5A57)'
          : 'linear-gradient(90deg, #C4A5A5, #B89999)',
        position: 'relative'
      };

    case INDICATOR_STYLES.MINIMAL:
    default:
      return {
        borderTop: '3px solid #ff4444',
        position: 'relative'
      };
  }
};

/**
 * Get environment badge styling
 */
export const getEnvironmentBadgeStyling = (isDarkMode, envInfo) => {
  if (envInfo.isProduction) {
    return {
      padding: '4px 12px',
      borderRadius: '6px',
      background: 'linear-gradient(45deg, #667eea, #764ba2)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      border: '1px solid rgba(102, 126, 234, 0.3)',
      backgroundColor: 'rgba(251, 251, 252, 0.1)', // §2.8: glass alpha tracks off-white
      backdropFilter: 'blur(10px)'
    };
  }

  // Non-production badge styling
  const baseStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    padding: '6px 16px',
    borderRadius: '8px',
    border: '2px solid rgba(255, 68, 68, 0.6)',
    backdropFilter: 'blur(5px)',
    fontWeight: 700,
    fontSize: '1rem',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    textShadow: '1px 1px 2px rgba(10, 11, 13, 0.5)' // §2.8: shadow alpha tracks near-black
  };

  switch (ACTIVE_STYLE) {
    case INDICATOR_STYLES.CONSTRUCTION:
      return {
        ...baseStyle,
        backgroundColor: 'rgba(255, 215, 0, 0.9)',
        // §2.8 exception: construction badge black on gold, semantic hazard pairing
        color: '#000000',
        border: '2px solid #000000',
        textShadow: 'none'
      };

    default:
      return {
        ...baseStyle,
        backgroundColor: isDarkMode ? 'rgba(139, 74, 71, 0.9)' : 'rgba(255, 68, 68, 0.9)',
        color: '#fbfbfc' // §2.8: off-white, not pure white
      };
  }
};

/**
 * Get pulsing indicator dot styling
 */
export const getPulsingDotStyling = () => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  // §2.8 exception (construction): black dot matches the hazard-tape pairing
  backgroundColor: ACTIVE_STYLE === INDICATOR_STYLES.CONSTRUCTION ? '#000000' : '#ff4444',
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.6, transform: 'scale(1.1)' },
    '100%': { opacity: 1, transform: 'scale(1)' }
  }
});